import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer die Seite "Zeiterfassung"
 * (`/settings/time-tracking`). Wird ueber den `?`-Button im Settings-
 * Sticky-Header gestartet.
 *
 * Sieben Schritte — Top-7 fachlicher Hebel aus 40+ Feldern:
 *  1. Welcome + Grundeinstellungen
 *  2. Master-Schalter "Zeiterfassung aktiv"
 *  3. Stempelkanaele (Terminal + Mobile)
 *  4. Maximale Tagesarbeitszeit (ArbZG)
 *  5. Pausenregeln
 *  6. Zeit runden (Lohn-Wirkung!)
 *  7. Frueh-Stempeln-Toleranz
 *
 * Tour-Texte in `de.json/en.json` unter `TUTORIAL.SETTINGS_TT.*`,
 * Voice-Over-Manifest unter `settings-time-tracking-help`.
 */
export const SETTINGS_TIME_TRACKING_HELP_TOUR: TourConfig = {
  id: TourFeature.SettingsTimeTrackingHelp,
  version: 1,
  title: 'SETTINGS_TT_HELP_TOUR_TITLE',
  description: 'SETTINGS_TT_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: { route: '/settings/time-tracking' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 140,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'SETTINGS_TT_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.TtBasicSection,
          spotlightAnchor: AnchorId.TtBasicSection,
          title: 'TUTORIAL.SETTINGS_TT.WELCOME.TITLE',
          content: 'TUTORIAL.SETTINGS_TT.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'master-switch',
          anchor: AnchorId.TtActiveToggle,
          spotlightAnchor: AnchorId.TtActiveToggle,
          title: 'TUTORIAL.SETTINGS_TT.MASTER_SWITCH.TITLE',
          content: 'TUTORIAL.SETTINGS_TT.MASTER_SWITCH.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'channels',
          // Seit 01.11.2026 ohne Zeile „Terminal aktivieren“ (Schalter nur noch auf der
          // Terminal-Seite); Anker ist deshalb die Zeile „Mobile Zeiterfassung“.
          // Neuer Sprechtext seit 02.11.2026 → neue Audio-ID (alter Ton nannte den Schalter).
          audioStepId: 'channels-v2',
          anchor: AnchorId.TtAllowMobile,
          spotlightAnchor: AnchorId.TtBasicSection,
          title: 'TUTORIAL.SETTINGS_TT.CHANNELS.TITLE',
          content: 'TUTORIAL.SETTINGS_TT.CHANNELS.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'max-daily-hours',
          anchor: AnchorId.TtMaxDailyHours,
          spotlightAnchor: AnchorId.TtWorkingHoursSection,
          title: 'TUTORIAL.SETTINGS_TT.MAX_DAILY.TITLE',
          content: 'TUTORIAL.SETTINGS_TT.MAX_DAILY.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="tt-max-daily-hours"]'),
        },
        {
          id: 'breaks',
          anchor: AnchorId.TtBreakTime,
          spotlightAnchor: AnchorId.TtBreaksSection,
          title: 'TUTORIAL.SETTINGS_TT.BREAKS.TITLE',
          content: 'TUTORIAL.SETTINGS_TT.BREAKS.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="tt-break-time"]'),
        },
        {
          id: 'ignore-early',
          anchor: AnchorId.TtIgnoreEarlyClockIn,
          spotlightAnchor: AnchorId.TtIgnoreEarlyClockIn,
          title: 'TUTORIAL.SETTINGS_TT.IGNORE_EARLY.TITLE',
          content: 'TUTORIAL.SETTINGS_TT.IGNORE_EARLY.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="tt-ignore-early-clock-in"]'),
        },
      ],
    },
  ],
};
